import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Import } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { getImports } from "@/api";
import { useMessageFiltering } from "@/hooks/useMessageFiltering";
import moment from "moment";

interface UseImportsViewController {
  imports: Import[];
  selectedImport?: Import;
  selectImport: (importId: string) => void;
  loading: boolean;
  context: string;
  filteredImports: Import[];
  clearFilter: () => void;
}

export const useImportsViewController = (): UseImportsViewController => {
  const [searchParams] = useSearchParams();
  const contextParam = searchParams.get("context");
  const typeFilter = searchParams.get("type");

  // Use the context from URL or default to "client"
  const context = contextParam || "client";

  const [selectedImport, setSelectedImport] = useState<Import | undefined>(
    undefined
  );
  const [imports, setImports] = useState<Import[]>([]);

  const navigate = useNavigate();
  const importId = searchParams.get("id");

  // Filter imports based on type parameter
  const filteredImports = useMemo(() => {
    // Start with all imports (no context filtering to match original behavior)
    let filtered = imports;
    
    // Apply type filter if specified
    if (typeFilter && typeFilter.trim()) {
      filtered = filtered.filter(imp => 
        imp.context?.toLowerCase().includes(typeFilter.toLowerCase())
      );
    }
    
    return filtered;
  }, [imports, typeFilter]);

  const clearFilter = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("type");
    navigate(`/app/imports?${newSearchParams.toString()}`, { replace: true });
  }, [navigate, searchParams]);

  // apply message updates to the import list
  useMessageFiltering((batch) => {
    setImports((prevState) => {
      // extract messages that we are interested in, taking the latest one
      const updates = Object.values(
        batch
          .filter((x) => x.messageType === "ImportUpdate")
          .reduce((acc: any, x) => {
            if (!acc[(x as any).importId]) {
              acc[(x as any).importId] = x;
            } else if (
              moment(acc[(x as any).importId].timeStamp).isBefore(x.timeStamp)
            ) {
              acc[(x as any).importId] = x;
            }
            return acc;
          }, {})
      );

      //  find any matches and update
      if (updates.length > 0) {
        let hasChanges = false;
        const newState = [...prevState];
        for (const update of updates) {
          const importToUpdate = newState.find(
            (x) => x.importId === (update as any).importId
          );
          if (importToUpdate) {
            hasChanges = true;
            importToUpdate.status = (update as any).status;
            importToUpdate.errors = (update as any).errors;
            importToUpdate.rowCount = (update as any).rowCount;
            importToUpdate.context = (update as any).context;
            importToUpdate.processedRows = (update as any).processedRows;
            importToUpdate.percentCompleted = percentCompleted(importToUpdate);
            
            // Update selectedImport if it matches the updated import
            if (selectedImport && selectedImport.importId === importToUpdate.importId) {
              setSelectedImport(importToUpdate);
            }
          }
        }
        if (hasChanges) {
          return newState;
        }
      }

      // no change
      return prevState;
    });
  });

  const result = useQuery({
    queryKey: ["imports"],
    queryFn: async () => {
      return await getImports();
    },
  });

  const selectImport = (importId: string) => {
    const importToSelect = result.data?.find(
      (imp) => imp.importId === importId
    );
    if (importToSelect) {
      setSelectedImport(importToSelect);
    }
    // Preserve the context and type parameters when navigating
    const typeParam = typeFilter ? `&type=${typeFilter}` : '';
    navigate(`/app/imports?id=${importId}&context=${context}${typeParam}`, {
      replace: true,
    });
  };

  const percentCompleted = useCallback(
    (importItem: Import): number | undefined => {
      if (importItem.status === "InProgress") {
        if (importItem.processedRows > 0 && importItem.rowCount > 0) {
          const result = Math.round(
            (importItem.processedRows / importItem.rowCount) * 100
          );
          if (!isNaN(result)) {
            return result;
          }
        }
        return 0;
      }
      return undefined;
    },
    []
  );

  useEffect(() => {
    if (result.data) {
      // sort the imports in descending order
      setImports(
        result.data.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      );
    }
  }, [result.data]);

  useEffect(() => {
    if (importId && result.data) {
      selectImport(importId);
    }
  }, [importId, result.data]);

  return {
    imports: filteredImports,
    selectedImport,
    loading: result.isLoading || result.isFetching,
    selectImport,
    context,
    filteredImports,
    clearFilter,
  };
};
